import { ChevronLeft, Search } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgTabPanel } from "@intelligems/ig-theme/IgTabPanel";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { CustomEventFeType } from "@intelligems/ig-types/dtos/analytics-events/custom-events";
import {
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  StandardEventAllowedKeyMetrics,
} from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import { CircularProgress, Drawer } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetAllCustomEvents } from "@src/api/custom-events/get-all-custom-events";
import { CreateCustomEvent } from "@src/features/customEvents/components/CreateCustomEvent";
import { CustomEventDrawerFooter } from "@src/features/customEvents/components/CustomEventDrawerFooter";
import { CustomEventSubpageWrapper } from "@src/features/customEvents/components/CustomEventSubpageWrapper";
import { SelectExistingCustomMetrics } from "@src/features/customEvents/components/SelectExistingCustomMetrics";
import { GenericAccordionSelectSections } from "@src/features/experienceMetrics/GenericAccordionSelectSections";
import { filterEventsByNameDescription } from "@src/features/experienceMetrics/helpers";
import { KeyMetricsDrawerTabs } from "@src/features/experienceMetrics/keyMetrics/KeyMetricsDrawerTabs";
import {
  initCustomEvents,
  initNewCustomEvent,
  resetNewCustomEvent,
  selectedCurrentCustomEvent,
} from "@src/store/slices/customEvents";
import { closeDrawer, isDrawerOpen } from "@src/store/slices/drawers";
import {
  getCustomKeyMetricEventIds,
  getStandardKeyMetricIds,
  setKeyMetrics,
} from "@src/store/slices/experienceMetrics/experienceMetrics";
import { closeProductPicker } from "@src/store/slices/productPicker";
import { selectedExperience } from "@src/store/slices/experiences";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import { getHasAnyOnsiteUpsellComponents } from "@src/store/slices/experienceComponents";

export const KeyMetricsDrawer = () => {
  const dispatch = useDispatch();
  const experience = useSelector(selectedExperience);
  const hasUpsellBlock = useSelector(getHasAnyUpsellBlocks());
  const hasOnsiteUpsell = useSelector(getHasAnyOnsiteUpsellComponents());
  const isKeyMetricDrawerOpen = useSelector(isDrawerOpen("keyMetrics"));
  const customEvents = useGetAllCustomEvents();
  const currentEvent = useSelector(selectedCurrentCustomEvent);
  const [search, setSearch] = useImmer<string>("");
  const [drawerPage, setDrawerPage] = useImmer<string>("list");

  const filteredEvents = useMemo(() => {
    let standardEvents = search.trim()
      ? filterEventsByNameDescription(
          STANDARD_EVENTS_ALLOWED_KEY_METRICS,
          search.toLowerCase()
        )
      : STANDARD_EVENTS_ALLOWED_KEY_METRICS;

    if (experience.type !== "postPurchase") {
      const {
        pctOrdersWithPostPurchaseOffer,
        netRevenueFromPostPurchaseOffer,
        ...filteredStandards
      } = standardEvents;
      standardEvents =
        filteredStandards as typeof STANDARD_EVENTS_ALLOWED_KEY_METRICS;
    }
    if (!hasUpsellBlock) {
      const {
        pctOrdersWithUpsell,
        netRevenueFromUpsell,
        ...filteredStandards
      } = standardEvents;
      standardEvents =
        filteredStandards as typeof STANDARD_EVENTS_ALLOWED_KEY_METRICS;
    }
    if (!hasOnsiteUpsell) {
      const {
        pctOrdersWithOnsiteUpsell,
        netRevenueFromOnsiteUpsell,
        ...filteredStandards
      } = standardEvents;
      standardEvents =
        filteredStandards as typeof STANDARD_EVENTS_ALLOWED_KEY_METRICS;
    }
    return {
      standardEvents,
      customEvents: search.trim()
        ? (customEvents?.data?.customEvents || []).filter(
            (event) =>
              event.name.toLowerCase().includes(search.toLowerCase()) ||
              (event?.description || "")
                .toLowerCase()
                .includes(search.toLowerCase())
          )
        : customEvents?.data?.customEvents || [],
    };
  }, [
    search,
    customEvents?.data?.customEvents,
    experience.type,
    hasUpsellBlock,
    hasOnsiteUpsell,
  ]);

  const [tabValue, setTabValue] = useImmer<number>(
    currentEvent !== undefined ? 1 : 0
  );
  const [isCreating, setIsCreating] = useImmer<boolean>(
    currentEvent !== undefined
  );
  const [subpageSelected, setSubpageSelected] =
    useImmer<CustomEventFeType | null>(
      currentEvent?.type ? (currentEvent.type as CustomEventFeType) : null
    );

  // standard event enum id
  const [selectedStandardMetricIds, setSelectedStandardMetricIds] = useImmer<
    StandardEventAllowedKeyMetrics[]
  >(useSelector(getStandardKeyMetricIds));
  // custom event uuids
  const [selectedCustomEventIds, setSelectedCustomEventIds] = useImmer<
    string[]
  >(useSelector(getCustomKeyMetricEventIds));

  // State for warning dialog
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningEventKey, setWarningEventKey] =
    useState<CustomEventFeType | null>(null);

  // Function to show warning dialog when a non-retroactive event is selected
  const handleShowWarningDialog = (eventKey: CustomEventFeType) => {
    setWarningEventKey(eventKey);
    setWarningDialogOpen(true);
  };

  // Function to handle proceeding after warning
  const handleProceedAfterWarning = () => {
    if (warningEventKey) {
      setSubpageSelected(warningEventKey);
      setDrawerPage("event");
    }
    setWarningDialogOpen(false);
  };

  const handleClose = () => {
    dispatch(resetNewCustomEvent());
    setIsCreating(false);
    setSubpageSelected(null);
    dispatch(closeDrawer("keyMetrics"));
  };

  // on drawer save
  const onSaveKeyMetrics = () => {
    dispatch(
      setKeyMetrics({
        standardEventList: selectedStandardMetricIds,
        selectedCustomEventIds,
        allCustomEvents: customEvents?.data?.customEvents || [],
      })
    );
    handleClose();
  };

  // Fetch data function
  useEffect(() => {
    if (customEvents?.data?.customEvents) {
      dispatch(
        initCustomEvents({ customEvents: customEvents.data.customEvents })
      );
    }
  }, [customEvents?.data?.customEvents]);

  // Init new event if isCreating is true and a subpage is selected
  useEffect(() => {
    if (isCreating && subpageSelected && !currentEvent) {
      dispatch(initNewCustomEvent({ eventType: subpageSelected }));
    }
  }, [subpageSelected]);
  const handleBackClick = () => {
    if (currentEvent?.id) {
      setIsCreating(false);
      setSubpageSelected(null);
      setDrawerPage("list");
    } else {
      setDrawerPage("subpage");
    }
    if (subpageSelected) {
      setSubpageSelected(null);
      dispatch(resetNewCustomEvent());
      dispatch(closeProductPicker());
    } else if (isCreating) {
      setIsCreating(false);
    }
  };
  return (
    <Drawer
      anchor="right"
      open={isKeyMetricDrawerOpen}
      onClose={handleClose}
      sx={{
        zIndex: 1400,
        ".MuiDrawer-paper": { borderRadius: "0 !important", width: "680px" },
      }}
    >
      {isCreating && (
        <IgButton
          variant={"text"}
          sx={{
            marginTop: "10px",
            marginLeft: "5px",
            justifyContent: "flex-start",
            width: "fit-content",
            alignItems: "center",
            paddingRight: "15px",
          }}
          startIcon={<ChevronLeft size={16} />}
          onClick={handleBackClick}
        >
          Back
        </IgButton>
      )}
      <Grid
        direction="column"
        container
        sx={{
          height: "100%",
          display: "flex",
          flexWrap: "nowrap",
        }}
      >
        {!isCreating && (
          <>
            <Grid
              container
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
              // marginTop={"20px"}
              sx={{ padding: "20px 40px" }}
            >
              <Grid>
                <IgTypography fontWeight={600} variant={"h2"}>
                  Select Key Metrics
                </IgTypography>
              </Grid>

              {tabValue === 0 ||
              (tabValue === 1 &&
                (customEvents?.data?.customEvents || [])?.length > 0) ? (
                <Grid>
                  <IgTextField
                    placeholder={"Search for metric"}
                    slotProps={{
                      input: {
                        endAdornment: <Search size={16} />,
                      },
                    }}
                    sx={{ width: "300px" }}
                    value={search ?? ""}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Grid>
              ) : null}
            </Grid>
            <Grid
              container
              direction={"row"}
              alignItems={"center"}
              columnSpacing={1}
              sx={{ padding: "0px 40px", marginTop: "-20px" }}
            >
              <KeyMetricsDrawerTabs
                tabValue={tabValue}
                setTabValue={setTabValue}
                selectedStandardMetrics={selectedStandardMetricIds}
                selectedCustomEventIds={selectedCustomEventIds}
              />
            </Grid>
            {tabValue === 1 &&
            (customEvents?.data?.customEvents || [])?.length === 0 ? null : (
              <IgDivider />
            )}
          </>
        )}

        <IgTabPanel value={tabValue} index={0}>
          <GenericAccordionSelectSections
            selectedStandardMetrics={selectedStandardMetricIds}
            setSelectedStandardMetrics={setSelectedStandardMetricIds}
            filteredStandardEvents={filteredEvents.standardEvents}
          />
        </IgTabPanel>

        <IgTabPanel value={tabValue} index={1} sx={{ height: "100%" }}>
          <Stack
            direction="column"
            sx={{
              height: "100%",
              padding: "0px 40px",
            }}
          >
            {customEvents?.isLoading ? (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                mt={5}
                sx={{ height: "100%" }}
              >
                <CircularProgress />
              </Grid>
            ) : (
              <Stack>
                <SelectExistingCustomMetrics
                  isCreating={isCreating}
                  keyMetricRender={true}
                  setIsCreating={setIsCreating}
                  customEventsData={filteredEvents.customEvents}
                  setSelectedCustomEventIds={setSelectedCustomEventIds}
                  selectedCustomEventIds={selectedCustomEventIds}
                  setSubpageSelected={setSubpageSelected}
                  setDrawerPage={setDrawerPage}
                  handleShowWarningDialog={handleShowWarningDialog}
                />
                <CreateCustomEvent
                  setSubpageSelected={setSubpageSelected}
                  isCreating={isCreating}
                  subpageSelected={subpageSelected}
                  setDrawerPage={setDrawerPage}
                  onShowWarningDialog={handleShowWarningDialog}
                />
                {subpageSelected && isCreating && (
                  <CustomEventSubpageWrapper eventType={subpageSelected} />
                )}
                {subpageSelected && isCreating && (
                  <CustomEventDrawerFooter
                    isCreating={isCreating}
                    setIsCreating={setIsCreating}
                    subpageSelected={subpageSelected}
                    setSubpageSelected={setSubpageSelected}
                    setSelectedGeneric={setSelectedCustomEventIds}
                    renderLocation={"keyMetric"}
                    selectedCustomEventIds={selectedCustomEventIds}
                    customEvents={customEvents?.data?.customEvents || []}
                    setDrawerPage={setDrawerPage}
                  />
                )}
              </Stack>
            )}
          </Stack>
        </IgTabPanel>
        {isCreating ? null : (
          <Grid
            container
            justifyContent={"flex-end"}
            spacing={2}
            sx={{
              ...(tabValue === 0 && {
                position: "sticky",
                bottom: 0,
                zIndex: 1,
                backgroundColor: "white",
              }),
              marginTop: "auto",
              paddingBottom: "20px",
              paddingLeft: "40px",
              paddingRight: "40px",
            }}
          >
            <Grid marginTop={"10px"}>
              <IgButton color={"secondary"} onClick={() => handleClose()}>
                Cancel
              </IgButton>
            </Grid>
            <Grid marginTop={"10px"}>
              <IgTooltip
                title={
                  selectedStandardMetricIds.length === 0 &&
                  selectedCustomEventIds.length === 0
                    ? "You must select at least one Key Metric to continue"
                    : selectedStandardMetricIds.length +
                          selectedCustomEventIds.length >
                        7
                      ? "A max of 7 Key Metrics can be added to a tests."
                      : ""
                }
              >
                <IgButton
                  variant={"contained"}
                  disabled={
                    (selectedStandardMetricIds.length === 0 &&
                      selectedCustomEventIds.length === 0) ||
                    selectedStandardMetricIds.length +
                      selectedCustomEventIds.length >
                      7
                  }
                  onClick={onSaveKeyMetrics}
                >
                  Save Key Metric
                  {selectedStandardMetricIds.length > 1 ? "s" : ""}
                </IgButton>
              </IgTooltip>
            </Grid>
          </Grid>
        )}
      </Grid>
      <IgDialog
        showDialog={warningDialogOpen}
        handleClose={() => setWarningDialogOpen(false)}
        title="Warning"
        content={
          <IgTypography>
            This type of metric can not be backfilled which can lead to
            imperfect data. Test has already started. Data for this metric will
            not be able to be applied to data already collected which could
            cause inconclusive results.
          </IgTypography>
        }
        yesButtonText="Proceed"
        noButtonText="Cancel"
        handleYesClick={handleProceedAfterWarning}
        handleNoClick={() => setWarningDialogOpen(false)}
      />
    </Drawer>
  );
};
